import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle, XCircle, CircleNotch, Warning, MapPin, WhatsappLogo, ArrowSquareOut, Clock, ArrowClockwise, Gift, ArrowRight } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 20;     // ~60s (20 x 3s) antes de cair no estado "processando"
const POLL_INTERVAL = 3000;
// Cartão usa escrow → status "authorized" (valor reservado) JÁ é sucesso.
const SUCCESS = ["paid", "authorized"];

function whatsappLink(phone, msg) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(msg)}`;
}
function googleMapsLink(a) {
  if (!a) return null;
  const q = `${a.street}, ${a.number} - ${a.neighborhood}, ${a.city} - ${a.uf}, ${a.cep}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const scheduled = params.get("scheduled") === "1";
  // Pagamento JÁ confirmado antes de chegar aqui (Saldo ou PIX): a tela recebe só `booking_id`,
  // sem `session_id`. Nesse caso é sucesso direto (não há polling de pré-autorização de cartão).
  const directConfirmed = !sessionId && !!bookingId && !scheduled;
  const [status, setStatus] = useState(scheduled ? "scheduled" : (directConfirmed ? "paid" : "pending")); // pending | paid | processing | failed | expired | scheduled
  const [booking, setBooking] = useState(null);
  const [payStatus, setPayStatus] = useState(directConfirmed ? "paid" : null);
  // V18.0 — Kit unificado: a fila legada (aj_kit_queue) foi substituída por checkout único.
  // Limpa qualquer resíduo de sessões anteriores para não oferecer "continuar" fantasma.
  const [kitQueue] = useState(() => {
    try { sessionStorage.removeItem("aj_kit_queue"); } catch { /* ignore */ }
    return null;
  });
  const continueKit = useCallback(() => { /* noop — kit já foi pago em uma cobrança */ }, []);

  const runPoll = useCallback(() => {
    if (scheduled && bookingId) {
      (async () => {
        try {
          const me = await api.get("/my/bookings");
          setBooking(me.data.find((x) => x.id === bookingId));
        } catch { /* ignore */ }
      })();
      return () => {};
    }
    if (directConfirmed && bookingId) {
      (async () => {
        try {
          const me = await api.get("/my/bookings");
          setBooking(me.data.find((x) => x.id === bookingId));
        } catch { /* ignore */ }
      })();
      return () => {};
    }
    if (!sessionId) { setStatus("failed"); return () => {}; }
    let active = true;
    setStatus("pending");

    const finishSuccess = async (ps) => {
      setPayStatus(ps);
      setStatus("paid");
      try {
        const me = await api.get("/my/bookings");
        setBooking(me.data.find((x) => x.session_id === sessionId));
      } catch { /* ignore */ }
    };

    const poll = async (n = 0) => {
      if (!active) return;
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        if (!active) return;
        if (SUCCESS.includes(data.payment_status)) return finishSuccess(data.payment_status);
        if (data.status === "expired") return setStatus("expired");
        if (data.payment_status === "refunded") return setStatus("failed");
        if (n + 1 >= MAX_ATTEMPTS) return setStatus("processing"); // não gira pra sempre
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      } catch {
        if (!active) return;
        if (n + 1 >= MAX_ATTEMPTS) return setStatus("processing");
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, directConfirmed, bookingId, scheduled]);

  useEffect(() => {
    const cleanup = runPoll();
    return cleanup;
  }, [runPoll]);

  const wa = booking && whatsappLink(booking.owner_phone, `Olá ${booking.owner_name}, sou ${booking.user_name} e fiz a reserva de ${booking.listing_title} no AlugaJá. Vamos combinar a retirada?`);
  const maps = booking && booking.address && googleMapsLink(booking.address);
  const isEscrow = payStatus === "authorized";

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="booking-success-page">
      <div className="aj-card p-8 text-center">
        {/* ---------- RESERVA AGENDADA (longa) ---------- */}
        {status === "scheduled" && (
          <div data-testid="success-state-scheduled">
            <Clock size={64} weight="fill" className="mx-auto text-[#004E98]" />
            <h1 className="font-heading text-2xl font-black mt-4">Reserva confirmada! 🗓️</h1>
            <p className="text-slate-600 mt-2 text-sm">
              Como a retirada é mais distante, <b>você ainda não foi cobrado</b>.
              {booking?.scheduled_charge_at ? (
                <> No dia <b>{new Date(booking.scheduled_charge_at).toLocaleDateString("pt-BR")}</b> vamos
                te avisar (aqui, push e e-mail) para <b>concluir o pagamento</b> (cartão, PIX ou saldo).</>
              ) : (
                <> Faltando <b>4 dias para a retirada</b> vamos te avisar para concluir o pagamento.</>
              )}
            </p>
            <div className="bg-[#004E98]/10 border border-[#004E98]/30 rounded-xl p-4 mt-5 text-left text-xs text-slate-700">
              Atenção: o pagamento precisa ser concluído <b>no mesmo dia do aviso</b>. Se não for pago,
              a reserva é cancelada e o item liberado para outras pessoas.
            </div>
            <Link to="/minhas-reservas" className="btn-primary w-full mt-6 inline-flex justify-center" data-testid="scheduled-go-bookings">
              Ver minhas reservas
            </Link>
          </div>
        )}

        {/* ---------- AGUARDANDO CONFIRMAÇÃO ---------- */}
        {status === "pending" && (
          <div data-testid="success-state-pending">
            <CircleNotch size={56} weight="bold" className="mx-auto animate-spin text-[#004E98]" />
            <h1 className="font-heading text-2xl font-black mt-4">Confirmando seu pagamento</h1>
            <p className="text-slate-500 mt-2 text-sm">
              Já estamos quase lá! Isso costuma levar só alguns segundos. Não feche esta tela. 🙂
            </p>
          </div>
        )}

        {/* ---------- SUCESSO ---------- */}
        {status === "paid" && (
          <div data-testid="success-state-paid">
            <CheckCircle size={64} weight="fill" className="mx-auto text-green-600" />
            <h1 className="font-heading text-2xl font-black mt-4">
              {isEscrow ? "Pagamento aprovado! 🎉" : "Pagamento confirmado! 🎉"}
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              {isEscrow
                ? "O valor ficou reservado no seu cartão e só será cobrado quando você retirar a ferramenta."
                : "Recebemos seu pagamento com sucesso."}
              <br />Agora é só aguardar o dono aceitar a sua reserva.
            </p>

            {booking?.token_retirada && (
              <div className="bg-[#FF6B35]/10 border-2 border-[#FF6B35] rounded-xl p-4 mt-5 text-left" data-testid="success-token-alert">
                <div className="flex items-center gap-2 font-bold text-[#FF6B35] text-sm">
                  <Warning size={18} weight="fill" /> IMPORTANTE: Guarde seu Token
                </div>
                <p className="text-xs text-slate-700 mt-1">
                  Você precisará informar este código de 4 dígitos ao proprietário no momento de retirar a ferramenta.
                </p>
                <div className="mt-3 bg-white rounded-lg p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Seu token de retirada</div>
                  <div className="font-heading font-black text-4xl tracking-[0.4em] text-[#FF6B35]" data-testid="success-token-code">
                    {booking.token_retirada}
                  </div>
                </div>
              </div>
            )}

            {booking?.address && (
              <div className="aj-card p-4 mt-4 text-left bg-slate-50" data-testid="success-address">
                <div className="font-bold text-sm flex items-center gap-1"><MapPin size={14} weight="bold" /> Local de retirada</div>
                <div className="text-xs text-slate-700 mt-1">
                  {booking.address.street}, {booking.address.number}<br />
                  {booking.address.neighborhood} · {booking.address.city} - {booking.address.uf}<br />
                  CEP {booking.address.cep}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {maps && (
                    <a href={maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#004E98] text-white rounded-full text-xs font-bold" data-testid="success-maps-btn">
                      <MapPin size={14} weight="fill" /> Ver no Maps <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  )}
                  {wa && (
                    <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-full text-xs font-bold" data-testid="success-whatsapp-btn">
                      <WhatsappLogo size={14} weight="fill" /> WhatsApp do dono
                    </a>
                  )}
                </div>
              </div>
            )}

            <Link to="/minhas-reservas" className="btn-primary mt-5 inline-flex" data-testid="go-bookings-btn">
              Ver minhas reservas
            </Link>
            {kitQueue && kitQueue.remaining?.length > 0 && (
              <button
                onClick={continueKit}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-emerald-500 bg-emerald-50 hover:bg-emerald-100 transition text-emerald-800 font-black py-3 text-sm"
                data-testid="continue-kit-btn"
              >
                <Gift size={18} weight="fill" />
                Continuar meu kit ({(kitQueue.done || 1) + 1} de {kitQueue.total || (kitQueue.remaining.length + 1)})
                <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        )}

        {/* ---------- AINDA PROCESSANDO (sem travar) ---------- */}
        {status === "processing" && (
          <div data-testid="success-state-processing">
            <Clock size={64} weight="fill" className="mx-auto text-[#FF6B35]" />
            <h1 className="font-heading text-2xl font-black mt-4">Estamos processando seu pagamento</h1>
            <p className="text-slate-600 mt-2 text-sm">
              Pode levar mais alguns instantes para a confirmação chegar.
              Fique tranquilo: assim que for aprovado, sua reserva aparecerá em <b>Minhas Reservas</b>.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <Link to="/minhas-reservas" className="btn-primary inline-flex justify-center" data-testid="processing-go-bookings-btn">
                Ir para Minhas Reservas
              </Link>
              <button
                onClick={() => runPoll()}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#004E98] py-2"
                data-testid="processing-recheck-btn"
              >
                <ArrowClockwise size={16} weight="bold" /> Verificar novamente
              </button>
            </div>
          </div>
        )}

        {/* ---------- FALHA ---------- */}
        {status === "failed" && (
          <div data-testid="success-state-failed">
            <XCircle size={64} weight="fill" className="mx-auto text-red-600" />
            <h1 className="font-heading text-2xl font-black mt-4">Não conseguimos confirmar o pagamento</h1>
            <p className="text-slate-600 mt-2 text-sm">
              Não recebemos a confirmação do seu pagamento. Fique tranquilo: <b>nada foi cobrado</b> — se o pagamento não for confirmado, nenhum valor é retido (no cartão, o valor fica apenas reservado e é liberado automaticamente).
              Você pode tentar novamente quando quiser.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <button onClick={() => navigate(-1)} className="btn-primary inline-flex justify-center" data-testid="failed-retry-btn">
                Tentar novamente
              </button>
              <Link to="/minhas-reservas" className="inline-flex items-center justify-center text-sm font-bold text-[#004E98] py-2" data-testid="failed-go-bookings-btn">
                Ver minhas reservas
              </Link>
            </div>
          </div>
        )}

        {/* ---------- SESSÃO EXPIRADA ---------- */}
        {status === "expired" && (
          <div data-testid="success-state-expired">
            <XCircle size={64} weight="fill" className="mx-auto text-red-600" />
            <h1 className="font-heading text-2xl font-black mt-4">Sessão expirada</h1>
            <p className="text-slate-600 mt-2 text-sm">O tempo para concluir o pagamento acabou. Você pode iniciar uma nova reserva.</p>
            <Link to="/" className="btn-primary mt-5 inline-flex" data-testid="expired-home-btn">Voltar ao início</Link>
          </div>
        )}
      </div>
    </div>
  );
}
